import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  MapPin, Phone, Calendar, Clock, CheckSquare, Square, Plus, Trash2,
  Camera, X, CheckCircle2, RefreshCw, FileText, ChevronLeft, Image,
  PlayCircle, Loader2, UserCheck, XCircle, CalendarClock,
  Navigation2, Truck, WifiOff, AlertCircle,
  FlaskConical, Target, ClipboardList, Layers, Package, Sparkles, RotateCcw,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../../../components/ui/sheet';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { Separator } from '../../../../components/ui/separator';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import CallButton from '../../../../components/clicktocall/CallButton';
import { toast } from 'sonner';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../../firebase';
import { useTasks } from '../../../../context/TasksContext';
import { useLeads } from '../../../../context/LeadsContext';
import { useSettings } from '../../../../context/SettingsContext';
import { useRole } from '../../../../context/RoleContext';
import {
  isActiveAccount,
  getAccountStatus,
  LIFECYCLE_PILL_CLASSES,
  LIFECYCLE_LABELS,
} from '../../../../utils/accountLifecycle';
import { useQuotes } from '../../../quotes/useQuotes';
import { useCompany } from '../../../../context/CompanyContext';
import { getDisplayName } from '../../../../lib/utils';
import { normalizeQuote } from '../../../../utils/normalizeQuote';
import JobAssignModal from '../../../../components/scheduler/JobAssignModal';
import RescheduleJobModal from '../../../../components/scheduler/RescheduleJobModal';
import FlashPill from '../../../../components/propertyDetails/FlashPill';
import PropertyDetailsCard from '../../../../components/propertyDetails/PropertyDetailsCard';
import OverrideChip from '../../../../components/overrides/OverrideChip';
import ConfirmCompleteDialog from '../../../../components/jobs/ConfirmCompleteDialog';
import PerVisitBillingOverride from '../../../../components/jobs/PerVisitBillingOverride';
import JobFinancialSection from './JobFinancialSection';
import NoChargeControl from '../../../jobs/NoChargeControl';
import DefaultItemsPicker from '../../../../components/catalog/DefaultItemsPicker';
import { resolveDisplayedStep } from '../../../../utils/resolveDisplayedStep';
import { getAuth } from 'firebase/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';

const JOB_COMPLETE_STAGE = 'Job Complete';
const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
};

const formatDuration = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  const mins = Math.round((new Date(endIso) - new Date(startIso)) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatAddress = (address) => {
  if (!address) return null;
  if (typeof address === 'string') return address;
  const { streetNumber, streetName, street, city, state, zip } = address;
  const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
  return [streetPart, city, state, zip].filter(Boolean).join(', ') || null;
};

const STATUS_STYLES = {
  approved:    { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Ready'       },
  pending:     { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Scheduled'   },
  scheduled:   { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Scheduled'   },
  en_route:    { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'En Route'    },
  on_site:     { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'On Site'     },
  in_progress: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'In Progress' },
  completed:   { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Completed'   },
  cancelled:   { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Cancelled'   },
};

// ---------------------------------------------------------------------------
// WorkOrderSheet
// ---------------------------------------------------------------------------

/**
 * WorkOrderSheet
 *
 * Full work order interface for on-site technicians.
 * Slides in as a Sheet from the right (full-screen on mobile).
 *
 * Props:
 *   task        — the job Task document
 *   client      — the client/lead object
 *   open        — boolean
 *   onClose     — fn()
 *   onCompleted — fn() called after Mark Job Complete
 */
const WorkOrderSheet = ({ task, client, open, onClose, onCompleted }) => {
  const { companyId } = useCompany();
  const { updateTask, completeJob } = useTasks();
  const { leads, updateLead } = useLeads();
  const { pipelineStages, addStage } = useSettings();
  const { isTechnician } = useRole();

  // Fetch linked quote for service details
  const { quotes } = useQuotes({ leadId: task?.leadId, companyId });
  const serviceQuote = quotes.find(q => q.status === 'approved') || quotes[0] || null;

  // Local work-order state (synced from task on open)
  const [checklist, setChecklist] = useState([]);
  const [newItemText, setNewItemText] = useState('');
  const [notes, setNotes] = useState('');
  const [noteSaved, setNoteSaved] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading]               = useState(false);
  const [uploadProgress, setUploadProgress]     = useState(0);
  const [completing, setCompleting]             = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [enRouting, setEnRouting]               = useState(false);
  const [onSiting, setOnSiting]                 = useState(false);
  const [cancelling, setCancelling]             = useState(false);
  const [assignModalOpen, setAssignModalOpen]   = useState(false);
  const [rescheduleOpen, setRescheduleOpen]     = useState(false);
  const [lightboxPhoto, setLightboxPhoto]       = useState(null); // { url } or null
  // Visit-only item override state — MVP items + visitInstructions.
  const [itemsCustomizeOpen, setItemsCustomizeOpen] = useState(false);
  const [customizeDraftItems, setCustomizeDraftItems] = useState([]);
  const [customizeVisitInstructions, setCustomizeVisitInstructions] = useState('');
  const [customizeReason, setCustomizeReason]   = useState('');
  const [savingOverride, setSavingOverride]     = useState(false);
  const [undoingOverride, setUndoingOverride]   = useState(false);

  // Offline resilience state
  const [isOnline, setIsOnline]           = useState(() => navigator.onLine);
  const [failedPhotos, setFailedPhotos]   = useState([]); // File objects that failed upload in this session
  const [noteSaveError, setNoteSaveError] = useState(false); // true when last save attempt failed
  // localStorage draft state — survives reloads / tab closes when offline or
  // when the tech navigates away before Firestore save completes.
  const [pendingDraft, setPendingDraft] = useState(null); // { text, ts } when stored draft differs from Firestore value
  const draftTimerRef = useRef(null);

  const fileInputRef = useRef(null);

  // Draft key helper — scoped per task so multiple open jobs don't collide.
  const draftKey = task?.id ? `kro:workorder:notes:${task.id}` : null;

  // Sync local state when task changes or sheet opens
  useEffect(() => {
    if (open && task) {
      setChecklist(task.checklist || []);
      const firestoreNotes = task.workOrderNotes || '';
      setNotes(firestoreNotes);
      setPhotos(task.photos || []);
      setNoteSaved(true);
      setNoteSaveError(false);
      setFailedPhotos([]);

      // Detect a pending localStorage draft from a previous session that
      // never made it into Firestore (e.g. app backgrounded while offline).
      try {
        const key = `kro:workorder:notes:${task.id}`;
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.text === 'string' && parsed.text !== firestoreNotes) {
            setPendingDraft({ text: parsed.text, ts: parsed.ts || null });
          } else {
            // Draft matches Firestore — no longer pending, clean up.
            localStorage.removeItem(key);
            setPendingDraft(null);
          }
        } else {
          setPendingDraft(null);
        }
      } catch {
        setPendingDraft(null);
      }
    }
  }, [open, task?.id]);

  // Debounced localStorage autosave of the in-progress note edit. Runs only
  // while notes diverge from the last Firestore-saved value. Cleared once
  // Firestore catches up (see handleSaveNotes) or the user discards the draft.
  useEffect(() => {
    if (!open || !draftKey) return;
    if (noteSaved) return; // nothing to draft — Firestore is current
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ text: notes, ts: Date.now() }));
      } catch {
        // Quota exceeded or localStorage unavailable — fail silent, the tech
        // isn't meaningfully worse off than before this feature existed.
      }
    }, 400);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [notes, noteSaved, open, draftKey]);

  // Track real-time network status for offline resilience
  useEffect(() => {
    const handleOnline  = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Checklist handlers
  // ---------------------------------------------------------------------------

  const handleAddItem = async () => {
    const text = newItemText.trim();
    if (!text || !task) return;
    const item = { id: crypto.randomUUID(), text, checked: false };
    const updated = [...checklist, item];
    setChecklist(updated);
    setNewItemText('');
    await updateTask(task.id, { checklist: updated }).catch(console.error);
  };

  const handleToggleItem = async (id) => {
    const updated = checklist.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setChecklist(updated);
    await updateTask(task.id, { checklist: updated }).catch(console.error);
  };

  const handleDeleteItem = async (id) => {
    const updated = checklist.filter(item => item.id !== id);
    setChecklist(updated);
    await updateTask(task.id, { checklist: updated }).catch(console.error);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAddItem();
  };

  // ---------------------------------------------------------------------------
  // Visit-only item override handlers
  // ---------------------------------------------------------------------------

  // Hydrate draft state when opening the customize dialog. Prefer the
  // existing override, otherwise fall back to the baseline items so the
  // operator sees the current state and modifies from there.
  useEffect(() => {
    if (!itemsCustomizeOpen || !task) return;
    const override = task.servicePlanStepOverride;
    const source = (override && Array.isArray(override.items))
      ? override.items
      : (Array.isArray(task.servicePlanStep?.items) ? task.servicePlanStep.items : []);
    setCustomizeDraftItems(source.map((it) => ({
      itemId: it.itemId,
      quantity: it.quantity ?? null,
    })));
    // Visit-specific instructions are override-only (no baseline fallback).
    setCustomizeVisitInstructions(
      typeof override?.visitInstructions === 'string' ? override.visitInstructions : ''
    );
    setCustomizeReason(override?.reason || '');
  }, [itemsCustomizeOpen, task?.id, task?.servicePlanStepOverride]);

  const handleSaveItemOverride = async () => {
    if (!task || !companyId) return;
    setSavingOverride(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      // Send visitInstructions only when the operator provided any
      // content OR is intentionally clearing an existing override string.
      // If neither the field was touched nor an existing override string
      // exists, omit the key so absent stays absent.
      const existingHasInstructions =
        typeof task?.servicePlanStepOverride?.visitInstructions === 'string';
      const trimmed = (customizeVisitInstructions || '').replace(/\s+$/g, '');
      const includeInstructions = existingHasInstructions || trimmed.length > 0;
      await axios.post(
        `${API}/api/tasks/${task.id}/item-override`,
        {
          companyId,
          items: customizeDraftItems.map((r) => ({
            itemId: r.itemId,
            quantity: r.quantity ?? null,
          })),
          visitInstructions: includeInstructions ? trimmed : null,
          reason: (customizeReason || '').trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Visit customized');
      setItemsCustomizeOpen(false);
    } catch (err) {
      console.error('[WorkOrderSheet] override save failed:', err);
      const msg = err?.response?.data?.detail || 'Failed to save customization';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save customization');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleUndoItemOverride = async () => {
    if (!task || !companyId || undoingOverride) return;
    if (!window.confirm('Undo customization? This visit will fall back to the program defaults it was generated with.')) return;
    setUndoingOverride(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      await axios.delete(`${API}/api/tasks/${task.id}/item-override`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Visit customization undone');
    } catch (err) {
      console.error('[WorkOrderSheet] override undo failed:', err);
      const msg = err?.response?.data?.detail || 'Failed to undo customization';
      toast.error(typeof msg === 'string' ? msg : 'Failed to undo customization');
    } finally {
      setUndoingOverride(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Notes handler
  // ---------------------------------------------------------------------------

  const handleSaveNotes = async (opts = {}) => {
    const { silent = false } = opts;
    if (!task) return;
    if (noteSaved) return;
    try {
      await updateTask(task.id, { workOrderNotes: notes });
      setNoteSaved(true);
      setNoteSaveError(false);
      // Firestore is now source of truth — drop the localStorage draft.
      try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* noop */ }
      if (!silent) toast.success('Notes saved');
    } catch (err) {
      console.error('[WorkOrderSheet] Save notes failed:', err);
      setNoteSaveError(true); // always surface — even on silent auto-save, indicator must appear
      if (!silent) toast.error('Failed to save notes');
    }
  };

  // Auto-save on blur — silent so the tech isn't spammed with toasts.
  const handleNotesBlur = () => { handleSaveNotes({ silent: true }); };

  // ---------------------------------------------------------------------------
  // Photo upload
  // ---------------------------------------------------------------------------

  // Core upload loop — shared by initial upload and in-session retry.
  // isRetry=true adjusts toast wording so the tech understands it's a retry attempt.
  const uploadFiles = async (filesToUpload, { isRetry = false } = {}) => {
    if (filesToUpload.length === 0 || !task || !storage) return;

    setUploading(true);
    setUploadProgress(0);

    const total = filesToUpload.length;
    let completed = 0;
    let currentBatch = [...photos];
    let failed = 0;
    const newFailedFiles = [];

    try {
      for (const file of filesToUpload) {
        try {
          const ext = file.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
          const storagePath = `companies/${companyId}/jobs/${task.id}/photos/${fileName}`;
          const storageRef = ref(storage, storagePath);

          await new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on(
              'state_changed',
              (snap) => {
                const fileFrac = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
                const overall = Math.round(((completed + fileFrac) / total) * 100);
                setUploadProgress(overall);
              },
              reject,
              async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                const newPhoto = { id: crypto.randomUUID(), url, storagePath, uploadedAt: new Date().toISOString() };
                currentBatch = [...currentBatch, newPhoto];
                // Persist after each successful upload so a mid-batch crash keeps earlier photos
                setPhotos(currentBatch);
                await updateTask(task.id, { photos: currentBatch });
                resolve();
              }
            );
          });
        } catch (fileErr) {
          console.error('[WorkOrderSheet] Photo upload failed for file:', file.name, fileErr);
          newFailedFiles.push(file); // preserve File object for in-session retry
          failed += 1;
        } finally {
          completed += 1;
          setUploadProgress(Math.round((completed / total) * 100));
        }
      }

      // Replace (not append) so retry correctly represents current failure set
      setFailedPhotos(newFailedFiles);

      const succeeded = total - failed;
      if (succeeded > 0 && failed === 0) {
        toast.success(isRetry
          ? (succeeded === 1 ? 'Photo uploaded' : `${succeeded} photos uploaded`)
          : (succeeded === 1 ? 'Photo added' : `${succeeded} photos added`)
        );
      } else if (succeeded > 0 && failed > 0) {
        toast.warning(isRetry
          ? `${succeeded} retried · ${failed} still failing`
          : `${succeeded} added · ${failed} failed — tap Retry to try again`
        );
      } else {
        toast.error(isRetry
          ? 'Still failing — check your signal and try again'
          : 'Upload failed — tap Retry to try again'
        );
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!isOnline) {
      toast.error('No signal — connect to upload photos');
      return;
    }
    await uploadFiles(files);
  };

  // Retry photos that failed in this session — uses stored File objects so
  // the tech never needs to re-select from their camera roll.
  const handleRetryPhotos = async () => {
    if (failedPhotos.length === 0) return;
    if (!isOnline) {
      toast.error('No signal — connect to retry upload');
      return;
    }
    const toRetry = [...failedPhotos];
    setFailedPhotos([]); // optimistic clear; repopulated by uploadFiles if failures persist
    await uploadFiles(toRetry, { isRetry: true });
  };

  const handleDeletePhoto = async (photo) => {
    try {
      if (photo.storagePath) {
        await deleteObject(ref(storage, photo.storagePath)).catch(() => {});
      }
      const updated = photos.filter(p => p.id !== photo.id);
      setPhotos(updated);
      await updateTask(task.id, { photos: updated });
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Failed to remove photo');
    }
  };

  // ---------------------------------------------------------------------------
  // Mark Job Complete
  // ---------------------------------------------------------------------------

  const handleMarkComplete = async () => {
    if (!task) return;

    // Fix C: Block completion when offline — the job record must be persisted server-side
    if (!isOnline) {
      toast.error('No signal — connect before marking complete');
      return;
    }

    if (uploading) {
      toast.info('Photo still uploading — hang on a sec…');
      return;
    }

    // Fix E: Block completion when there are failed photos in the retry queue.
    // Data integrity: a job must not be closed while site photos are missing.
    if (failedPhotos.length > 0) {
      toast.error(
        `${failedPhotos.length} photo${failedPhotos.length !== 1 ? 's' : ''} failed to upload — retry or remove before completing`
      );
      return;
    }

    // Fix B: Force-save notes before completing.
    // ABORT if the save fails — notes must never be silently lost on job completion.
    if (!noteSaved) {
      try {
        await updateTask(task.id, { workOrderNotes: notes });
        setNoteSaved(true);
        setNoteSaveError(false);
      } catch (err) {
        console.error('[WorkOrderSheet] Pre-complete notes save failed:', err);
        setNoteSaveError(true);
        toast.warning('Notes could not be saved — resolve before marking complete');
        return; // ABORT: do not complete without persisting notes
      }
    }

    setCompleting(true);
    try {
      const quoteId = serviceQuote?.quoteId || serviceQuote?.id;

      // Sprint 4 — single completion contract. Always route through the
      // canonical completeJob helper, which POSTs to the backend so the
      // recurrence engine, override consumption, and invoice / SMS
      // pipeline run as one atomic transaction. No more inline fetch.
      let data;
      try {
        data = await completeJob(task.id, {
          quoteId: quoteId || null,
          originUrl: window.location.origin,
        });
      } catch (err) {
        console.error('[WorkOrderSheet] completeJob failed:', err);
        toast.error('Could not mark job complete', {
          description: err?.message || 'Please retry. Recurring scheduling depends on this.',
          duration: 10000,
        });
        return;
      }

      if (data?.isNonBillable) {
        toast.success('Job marked complete — No Charge. No invoice generated.');
      } else if (data?.billingWarning) {        toast.warning(
          data.message || 'Job marked complete, but invoice generation failed.',
          {
            description: data.billingWarning === 'stripe_not_configured'
              ? 'Configure Stripe in Settings → Billing.'
              : (data.billingError || 'Open Invoices to retry — the failed entry is saved there.'),
            action: { label: 'Open Invoices', onClick: () => { window.location.href = '/invoices'; } },
            duration: 12000,
          }
        );
      } else if (data?.invoice && !data?.alreadyExists) {
        toast.success('Final invoice created — payment link sent to customer!', {
          action: { label: 'View Invoices', onClick: () => { window.location.href = '/invoices'; } },
          duration: 8000,
        });
      } else if (data?.alreadyExists) {
        toast.info('Final invoice already exists.', {
          action: { label: 'View Invoices', onClick: () => { window.location.href = '/invoices'; } },
          duration: 6000,
        });
      } else if (quoteId) {
        toast.info(data?.message || 'Job complete. No invoice needed.');
      } else {
        toast.success('Job marked as complete!');
      }

      // Update lead stage to "Job Complete" for accurate pipeline tracking
      if (task.leadId) {
        const stageExists = pipelineStages.includes(JOB_COMPLETE_STAGE);
        if (!stageExists) {
          try {
            await addStage(JOB_COMPLETE_STAGE);
          } catch (stageErr) {
            console.warn('[WorkOrderSheet] Could not add pipeline stage:', stageErr?.message);
          }
        }
        await updateLead(task.leadId, { stage: JOB_COMPLETE_STAGE });
      }

      onCompleted?.();
      onClose();
    } catch (err) {
      console.error('[WorkOrderSheet] Mark complete error:', err);
      toast.error('Failed to mark job complete');
    } finally {
      setCompleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cancel Job
  // ---------------------------------------------------------------------------

  const handleCancelJob = async () => {
    if (!task) return;
    const confirmed = window.confirm(
      'Cancel this job? It will be removed from the active schedule but remain in job history.'
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      await updateTask(task.id, {
        status: 'cancelled',
        scheduledStart: null,
        scheduledEnd: null,
      });
      toast.success('Job cancelled');
      onClose();
    } catch (err) {
      console.error('[WorkOrderSheet] Cancel job error:', err);
      toast.error('Failed to cancel job');
    } finally {
      setCancelling(false);
    }
  };

  // ---------------------------------------------------------------------------
  // En Route — tech is on the way
  // ---------------------------------------------------------------------------

  const handleEnRoute = async () => {
    if (!task) return;
    setEnRouting(true);
    try {
      await updateTask(task.id, {
        status:     'en_route',
        enRouteAt:  new Date().toISOString(),
      });
      toast.success('On the way!');
    } catch (err) {
      console.error('[WorkOrderSheet] En route error:', err);
      toast.error('Failed to update status');
    } finally {
      setEnRouting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // On Site — tech has arrived
  // ---------------------------------------------------------------------------

  const handleOnSite = async () => {
    if (!task) return;
    setOnSiting(true);
    try {
      await updateTask(task.id, {
        status:     'on_site',
        arrivedAt:  new Date().toISOString(),
      });
      toast.success('Marked as On Site');
    } catch (err) {
      console.error('[WorkOrderSheet] On site error:', err);
      toast.error('Failed to update status');
    } finally {
      setOnSiting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Undo On Site — revert an accidental arrival back to En Route
  // ---------------------------------------------------------------------------
  //
  // Deliberately narrow: only allowed from `on_site`. Not exposed from
  // `in_progress` (work has started) or `completed` (terminal). Clears
  // `arrivedAt` so the audit trail reflects that arrival was mistakenly
  // logged; `enRouteAt` is preserved (the tech is still on the way).

  const handleUndoOnSite = async () => {
    if (!task || task.status !== 'on_site') return;
    try {
      await updateTask(task.id, {
        status:    'en_route',
        arrivedAt: null,
      });
      toast.success('Reverted to En Route');
    } catch (err) {
      console.error('[WorkOrderSheet] Undo on-site error:', err);
      toast.error('Failed to revert status');
    }
  };

  if (!task) return null;

  const status = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  const isCompleted = task.status === 'completed';
  const checkedCount = checklist.filter(i => i.checked).length;
  const customerName = getDisplayName(client, 'Customer');
  const address = formatAddress(client?.address);
  const phone = client?.phone || client?.phoneNumber || '';

  // Build service lines using the unified normalizeQuote() utility.
  // Handles both Calculator quotes (services{}) and Workspace quotes (lineItems[]).
  const serviceLines = normalizeQuote(serviceQuote)?.lineItems?.map(item => ({
    name: item.name,
    amount: item.total ?? item.price ?? item.unitPrice ?? null,
  })) ?? [];

  return (
    <>
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 flex flex-col"
        data-testid="work-order-sheet"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              data-testid="work-order-close-btn"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold truncate leading-tight">
                {task.title || 'Work Order'}
              </SheetTitle>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <p className="text-xs text-gray-500">{customerName}</p>
                {/* Phase E: passive lifecycle pill — read-only, never blocks
                    operator actions. Only renders for paused/closed accounts. */}
                {client && !isActiveAccount(client) && (
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${LIFECYCLE_PILL_CLASSES[getAccountStatus(client)]}`}
                    data-testid="work-order-lifecycle-pill"
                    title="Account lifecycle status — automated actions may be suppressed. Manual actions remain available."
                  >
                    {LIFECYCLE_LABELS[getAccountStatus(client)]}
                  </span>
                )}
              </div>
            </div>
            {/* Assign Technician */}
            {!isCompleted && (
              <button
                type="button"
                data-testid="assign-tech-btn"
                onClick={() => setAssignModalOpen(true)}
                title={task.assignedToName ? `Assigned: ${task.assignedToName}` : 'Assign Technician'}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium
                  border transition-colors shrink-0
                  ${task.assignedTo
                    ? 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'
                    : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
              >
                <UserCheck className="w-3.5 h-3.5" />
                <span className="max-w-[72px] truncate">
                  {task.assignedToName ? task.assignedToName.split(' ')[0] : 'Assign'}
                </span>
              </button>
            )}
            <span className={`px-2 py-1 rounded-full text-xs font-semibold shrink-0 ${status.bg} ${status.text}`}>
              {status.label}
            </span>
          </div>
        </SheetHeader>

        {/* Offline indicator — visible whenever the device loses connectivity */}
        {!isOnline && (
          <div
            className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium"
            data-testid="offline-banner"
          >
            <WifiOff className="w-3.5 h-3.5 shrink-0" />
            No signal — changes may not save until you're back online
          </div>
        )}

        {/* Property & Access — bright flash pill + collapsible details. Stays
            visible after completion since these are property-level facts. */}
        {client?.propertyDetails && (
          (client.propertyDetails.flashNote ||
           client.propertyDetails.gateCode ||
           client.propertyDetails.parkingNotes ||
           client.propertyDetails.entryInstructions ||
           client.propertyDetails.petOnSite ||
           client.propertyDetails.alarmInfo ||
           client.propertyDetails.siteHazards) && (
            <div className="shrink-0 px-3 pt-3 space-y-2" data-testid="work-order-property-details">
              {client.propertyDetails.flashNote && (
                <FlashPill
                  propertyDetails={client.propertyDetails}
                  testId="work-order-flash-pill"
                  className="max-w-full"
                />
              )}
              <PropertyDetailsCard
                leadOrClient={client}
                defaultOpen={false}
              />
            </div>
          )
        )}

        {/* Next-service override chip — pricing context for the office before
            they fire Charge Now. Stays visible until the task is completed. */}
        {client?.recurrence?.nextServiceOverride && task?.id && (
          <div className="shrink-0 px-3 pt-2" data-testid="work-order-override">
            <OverrideChip
              override={client.recurrence.nextServiceOverride}
              recurringAmount={client.recurrence.amount}
              taskId={task.id}
              matchesEarliest={true}
              testId="work-order-override-chip"
              className="max-w-full"
            />
          </div>
        )}

        {/* ── Quick Action Bar — always visible on open ───────────────────
             Call · Directions · Primary status action (en route/on site/complete)
             44px+ touch targets. Disabled with gray state when data unavailable.  */}
        {!isCompleted && task.status !== 'cancelled' && (
          <div
            className="shrink-0 border-b bg-gray-50/80 px-3 py-2.5"
            data-testid="quick-action-bar"
          >
            <div className="flex items-center gap-2">

              {/* Call Client — uses shared CallButton (auto-routes browser vs. phone bridge) */}
              {phone ? (
                <CallButton
                  phone={phone}
                  surface="work-order-quick-action"
                  className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 hover:bg-gray-50 active:scale-[0.97] transition-all shadow-sm"
                  data-testid="quick-call-btn"
                  title="Call client"
                >
                  <Phone className="w-4 h-4 text-green-600" />
                  <span>Call</span>
                </CallButton>
              ) : (
                <div className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-300 cursor-not-allowed shadow-sm">
                  <Phone className="w-4 h-4" />
                  <span>Call</span>
                </div>
              )}

              {/* Directions */}
              {address ? (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 hover:bg-gray-50 active:scale-[0.97] transition-all shadow-sm"
                  data-testid="quick-directions-btn"
                >
                  <Navigation2 className="w-4 h-4 text-blue-500" />
                  <span>Directions</span>
                </a>
              ) : (
                <div className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-300 cursor-not-allowed shadow-sm">
                  <Navigation2 className="w-4 h-4" />
                  <span>Directions</span>
                </div>
              )}

              {/* Primary status action */}
              {['approved', 'scheduled', 'pending'].includes(task.status) && (
                <button
                  type="button"
                  onClick={handleEnRoute}
                  disabled={enRouting}
                  className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold active:scale-[0.97] transition-all shadow-sm disabled:opacity-60"
                  data-testid="quick-en-route-btn"
                >
                  {enRouting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Truck className="w-4 h-4" />}
                  <span>En Route</span>
                </button>
              )}

              {task.status === 'en_route' && (
                <button
                  type="button"
                  onClick={handleOnSite}
                  disabled={onSiting}
                  className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold active:scale-[0.97] transition-all shadow-sm disabled:opacity-60"
                  data-testid="quick-on-site-btn"
                >
                  {onSiting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <MapPin className="w-4 h-4" />}
                  <span>On Site</span>
                </button>
              )}

              {(task.status === 'on_site' || task.status === 'in_progress') && (
                <button
                  type="button"
                  onClick={() => setConfirmCompleteOpen(true)}
                  disabled={completing || failedPhotos.length > 0}
                  title={failedPhotos.length > 0 ? 'Retry or remove failed photos first' : undefined}
                  className="flex items-center justify-center gap-1.5 flex-1 min-h-[44px] bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold active:scale-[0.97] transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="quick-complete-btn"
                >
                  {completing
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4" />}
                  <span>Complete</span>
                </button>
              )}

            </div>
          </div>
        )}

        {/* Scrollable body */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 py-4 space-y-5 pb-32">

            {/* Phase 2B — derived financial status (completed jobs only) */}
            {isCompleted && (
              <JobFinancialSection companyId={companyId} task={task} leadId={task.leadId} />
            )}

            {/* Billing Intent — No Charge override (active service jobs only,
                before any financial outcome is created). */}
            {!isCompleted && task.status !== 'cancelled' && task.type === 'job' && (
              <NoChargeControl task={task} />
            )}

            {/* Scheduled Time */}
            {(task.scheduledStart || task.scheduledEnd) && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <Calendar className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    {formatDateTime(task.scheduledStart)}
                  </p>
                  {task.scheduledEnd && (
                    <p className="text-xs text-blue-600">
                      Duration: {formatDuration(task.scheduledStart, task.scheduledEnd)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Service Program Step ─────────────────────────────────────
                Read-only render of the servicePlanStep snapshot stamped on
                this recurring task at materialisation time (see
                servicePlanSeeding.js / BookVisitModal / backend recurrence
                engine). Presentation-layer only — no writes, no fetches.

                Renders only when this visit has a snapshot. One-time jobs
                and any legacy recurring task created before snapshots were
                being written will simply skip the entire section. */}
            {task.servicePlanStep && (
              (() => {
                const { displayed, hasOverride } = resolveDisplayedStep(task);
                const step = displayed;
                const hasItems      = Array.isArray(step.items) && step.items.length > 0;
                const hasProducts   = Array.isArray(step.products) && step.products.length > 0;
                const hasAreas      = Array.isArray(step.areas)    && step.areas.length > 0;
                const hasTechNotes  = typeof step.techNotes  === 'string' && step.techNotes.trim().length  > 0;
                const hasOfficeNotes= typeof step.officeNotes=== 'string' && step.officeNotes.trim().length> 0;
                const hasDuration   = Number.isFinite(step.estimatedDurationMin) && step.estimatedDurationMin > 0;
                // visitInstructions is override-only; string presence (incl. "") means "show the section"
                const hasVisitInstructions = typeof step.visitInstructions === 'string';
                const showItemsBlock = hasItems || (hasOverride && Array.isArray(task.servicePlanStepOverride?.items));
                const hasAnyBody    = showItemsBlock || hasVisitInstructions || hasProducts || hasAreas || hasTechNotes || (hasOfficeNotes && !isTechnician);
                const visitLabel    = task.visitNumber ? `Visit ${task.visitNumber}` : null;
                const stepLabel     = (step.label || '').trim();
                const showHeader    = task.programName || stepLabel || visitLabel;
                const isTerminal    = task.status === 'completed' || task.status === 'cancelled';
                const canCustomize  = !isTechnician && !isTerminal;
                if (!showHeader && !hasAnyBody && !hasDuration) return null;
                return (
                  <div
                    className="rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden"
                    data-testid="work-order-program-step"
                  >
                    <div className="px-3 py-2 flex items-start gap-3 border-b border-indigo-100 bg-indigo-50/60">
                      <Layers className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        {task.programName && (
                          <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide truncate">
                            {task.programName}
                          </p>
                        )}
                        <p
                          className="text-sm font-semibold text-indigo-900 truncate"
                          data-testid="work-order-step-label"
                        >
                          {stepLabel || visitLabel || 'Service Step'}
                          {stepLabel && visitLabel ? (
                            <span className="ml-2 text-xs font-normal text-indigo-600">· {visitLabel}</span>
                          ) : null}
                        </p>
                        {hasDuration && (
                          <p
                            className="text-xs text-indigo-700 mt-0.5 flex items-center gap-1"
                            data-testid="work-order-step-duration"
                          >
                            <Clock className="w-3 h-3" />
                            Estimated {step.estimatedDurationMin} min
                          </p>
                        )}
                        {hasOverride && (
                          <p
                            className="text-[11px] font-semibold text-amber-700 mt-1 flex items-center gap-1"
                            data-testid="work-order-step-customized-chip"
                          >
                            <Sparkles className="w-3 h-3" />
                            Customized for this visit
                          </p>
                        )}
                      </div>
                      {canCustomize && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setItemsCustomizeOpen(true)}
                            className="text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100 rounded-md px-2 py-1 transition-colors"
                            data-testid="work-order-customize-items-btn"
                          >
                            {hasOverride ? 'Edit items' : 'Customize this visit'}
                          </button>
                          {hasOverride && (
                            <button
                              type="button"
                              onClick={handleUndoItemOverride}
                              disabled={undoingOverride}
                              className="text-[11px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md px-2 py-1 transition-colors flex items-center justify-center gap-1"
                              data-testid="work-order-undo-customize-btn"
                            >
                              <RotateCcw className="w-3 h-3" />
                              {undoingOverride ? 'Undoing…' : 'Undo customization'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {hasAnyBody && (
                      <div className="px-3 py-3 space-y-3">
                        {showItemsBlock && (
                          <div data-testid="work-order-step-items">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                              <Package className="w-3 h-3" />
                              Items
                              {hasOverride && (
                                <span className="ml-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                  visit override
                                </span>
                              )}
                            </p>
                            {hasItems ? (
                              <ul className="text-sm text-gray-800 space-y-0.5 list-disc list-inside">
                                {step.items.map((it, i) => (
                                  <li key={`item-${i}`} data-testid={`work-order-step-item-${i}`}>
                                    {it.nameAtSnapshot || '(unknown item)'}
                                    {it.sku && <span className="ml-1 text-xs text-gray-400">· {it.sku}</span>}
                                    {(it.quantity != null || it.unit) && (
                                      <span className="ml-1 text-xs text-gray-500">
                                        {it.quantity != null ? `× ${it.quantity}` : ''}
                                        {it.unit ? ` ${it.unit}` : ''}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-500 italic" data-testid="work-order-step-items-empty">
                                No items for this visit
                              </p>
                            )}
                          </div>
                        )}

                        {hasVisitInstructions && (
                          <div
                            className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2"
                            data-testid="work-order-visit-instructions"
                          >
                            <p className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                              <Sparkles className="w-3 h-3" />
                              Visit-Specific Instructions
                            </p>
                            {step.visitInstructions.trim().length > 0 ? (
                              <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
                                {step.visitInstructions}
                              </p>
                            ) : (
                              <p className="text-xs text-amber-700 italic">
                                Instructions cleared for this visit
                              </p>
                            )}
                            <p className="text-[10px] text-amber-700/80 mt-1">
                              Applies only to this visit. Program and future visits unchanged.
                            </p>
                          </div>
                        )}

                        {hasProducts && (
                          <div data-testid="work-order-step-products">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                              <FlaskConical className="w-3 h-3" />
                              Products / Materials
                            </p>
                            <ul className="text-sm text-gray-800 space-y-0.5 list-disc list-inside">
                              {step.products.map((p, i) => (
                                <li key={`prod-${i}`}>{p}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {hasAreas && (
                          <div data-testid="work-order-step-areas">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                              <Target className="w-3 h-3" />
                              Areas to Treat
                            </p>
                            <ul className="text-sm text-gray-800 space-y-0.5 list-disc list-inside">
                              {step.areas.map((a, i) => (
                                <li key={`area-${i}`}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {hasTechNotes && (
                          <div data-testid="work-order-step-tech-notes">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                              <ClipboardList className="w-3 h-3" />
                              Technician Instructions
                            </p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {step.techNotes}
                            </p>
                          </div>
                        )}

                        {/* Office Notes — visible only to non-technician roles.
                            These are dispatcher/CSR-facing per the field's
                            purpose in Settings → Service Programs. */}
                        {hasOfficeNotes && !isTechnician && (
                          <div data-testid="work-order-step-office-notes">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                              <FileText className="w-3 h-3" />
                              Office Notes
                            </p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {step.officeNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()
            )}

            {/* ── Site Photos — moved up so the camera is one tap away ─── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Site Photos
                </h3>
                <span className="text-xs text-gray-400" data-testid="photo-count">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Photo grid — tap thumbnail to zoom, tap × to delete (always visible on touch) */}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {photos.map(photo => (
                    <div
                      key={photo.id}
                      className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
                    >
                      <button
                        type="button"
                        onClick={() => setLightboxPhoto(photo)}
                        className="absolute inset-0 w-full h-full"
                        data-testid={`photo-thumb-${photo.id}`}
                        aria-label="View photo"
                      >
                        <img
                          src={photo.url}
                          alt="Site photo"
                          className="w-full h-full object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo); }}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md
                          opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                        data-testid={`photo-delete-${photo.id}`}
                        aria-label="Delete photo"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload input — multiple=true so a tech can select a batch in one shot */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={handlePhotoUpload}
                data-testid="photo-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!isOnline) { toast.error('No signal — connect to upload photos'); return; }
                  fileInputRef.current?.click();
                }}
                disabled={uploading || !storage}
                data-testid="upload-photo-btn"
                className="w-full border-dashed min-h-[44px]"
              >
                {uploading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Uploading {uploadProgress}%</>
                ) : (
                  <><Camera className="w-4 h-4 mr-2" />Add Photos</>
                )}
              </Button>
              {!storage && (
                <p className="text-xs text-amber-600 mt-1 text-center">
                  Photo upload requires Firebase Storage to be configured.
                </p>
              )}

              {/* In-session photo retry — shown when previous uploads failed in this session */}
              {failedPhotos.length > 0 && !uploading && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryPhotos}
                  data-testid="retry-photos-btn"
                  className="w-full mt-2 border-amber-300 text-amber-700 hover:bg-amber-50 min-h-[44px]"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry {failedPhotos.length} failed photo{failedPhotos.length !== 1 ? 's' : ''}
                </Button>
              )}
            </div>

            <Separator />

            {/* Customer Info */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Customer</h3>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">{customerName}</p>
                {address && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                    <span>{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 shrink-0 text-gray-400" />
                    <CallButton
                      phone={phone}
                      surface="work-order-contact-block"
                      className="text-sm text-blue-600 font-medium hover:underline inline-flex items-center"
                      data-testid="work-order-phone-link"
                      title="Call client"
                    >
                      {phone}
                    </CallButton>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Service Details */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Service Details
              </h3>
              {serviceLines.length > 0 ? (
                <div className="space-y-1.5">
                  {serviceLines.map((line, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{line.name}</span>
                      {line.amount != null && (
                        <span className="font-medium text-gray-900">
                          ${parseFloat(line.amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                  {serviceQuote?.totals?.grandTotal != null && (
                    <>
                      <Separator className="my-1" />
                      <div className="flex justify-between items-center text-sm font-bold">
                        <span>Total</span>
                        <span>${parseFloat(serviceQuote.totals.grandTotal).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-400 italic">
                  <FileText className="w-4 h-4" />
                  <span>No service details from a linked quote</span>
                </div>
              )}
            </div>

            <Separator />

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Checklist
                </h3>
                {checklist.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {checkedCount}/{checklist.length} done
                  </span>
                )}
              </div>

              {/* Checklist items */}
              <div className="space-y-1 mb-2">
                {checklist.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 py-1.5 group"
                    data-testid={`checklist-item-${item.id}`}
                  >
                    <button
                      onClick={() => handleToggleItem(item.id)}
                      className="shrink-0 text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      {item.checked
                        ? <CheckSquare className="w-5 h-5 text-blue-500" />
                        : <Square className="w-5 h-5" />}
                    </button>
                    <span className={`text-sm flex-1 ${item.checked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                      {item.text}
                    </span>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add item input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newItemText}
                  onChange={e => setNewItemText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add checklist item..."
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="checklist-add-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddItem}
                  disabled={!newItemText.trim()}
                  data-testid="checklist-add-btn"
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Notes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notes</h3>
                {noteSaveError && (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-600" data-testid="notes-save-error-indicator">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    Save failed — tap Save Notes
                  </span>
                )}
                {!noteSaved && !noteSaveError && (
                  <span className="text-xs text-amber-500" data-testid="notes-unsaved-indicator">
                    Unsaved changes
                  </span>
                )}
              </div>
              {pendingDraft && (
                <div
                  className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                  data-testid="unsaved-draft-banner"
                >
                  <AlertCircle className="mt-0.5 w-4 h-4 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-amber-900">
                      Unsaved draft from a previous session
                    </p>
                    <p className="text-[11px] text-amber-700 truncate">
                      “{pendingDraft.text.slice(0, 80)}{pendingDraft.text.length > 80 ? '…' : ''}”
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setNotes(pendingDraft.text);
                        setNoteSaved(false);
                        setPendingDraft(null);
                      }}
                      className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                      data-testid="restore-draft-btn"
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* noop */ }
                        setPendingDraft(null);
                      }}
                      className="text-xs text-amber-700 hover:text-amber-900"
                      data-testid="discard-draft-btn"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}
              <textarea
                value={notes}
                onChange={e => { setNotes(e.target.value); setNoteSaved(false); setNoteSaveError(false); }}
                onBlur={handleNotesBlur}
                rows={4}
                placeholder="Add job notes, special instructions, or observations..."
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                data-testid="work-order-notes"
              />
              <div className="flex justify-end mt-1.5">
                <Button
                  size="sm"
                  variant={noteSaved ? 'outline' : 'default'}
                  onClick={() => handleSaveNotes()}
                  disabled={noteSaved}
                  data-testid="save-notes-btn"
                  className={noteSaved ? 'text-gray-400' : ''}
                >
                  {noteSaved ? 'Saved' : 'Save Notes'}
                </Button>
              </div>
            </div>

          </div>
        </ScrollArea>

        {/* Sticky footer — secondary confirmation + admin-only actions */}

        {/* approved / scheduled / pending → En Route */}
        {['approved', 'scheduled', 'pending'].includes(task.status) && (
          <div className="shrink-0 border-t bg-white px-4 py-3 space-y-2" data-testid="work-order-footer">
            <Button
              className="w-full h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
              onClick={handleEnRoute}
              disabled={enRouting}
              data-testid="start-job-btn"
            >
              {enRouting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Updating...</>
              ) : (
                <><Truck className="w-5 h-5 mr-2" />En Route</>
              )}
            </Button>
            {!isTechnician && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-blue-600 border-blue-200 hover:bg-blue-50 rounded-xl"
                  onClick={() => setRescheduleOpen(true)}
                  data-testid="reschedule-job-btn"
                >
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                  Reschedule
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                  onClick={handleCancelJob}
                  disabled={cancelling}
                  data-testid="cancel-job-btn"
                >
                  {cancelling ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Cancelling...</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-1.5" />Cancel</>
                  )}
                </Button>
              </div>
            )}
            {isTechnician && (
              <Button
                variant="outline"
                className="w-full h-10 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                onClick={handleCancelJob}
                disabled={cancelling}
                data-testid="cancel-job-btn"
              >
                {cancelling ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancelling...</>
                ) : (
                  <><XCircle className="w-4 h-4 mr-2" />Cancel Job</>
                )}
              </Button>
            )}
          </div>
        )}

        {/* en_route → On Site */}
        {task.status === 'en_route' && (
          <div className="shrink-0 border-t bg-white px-4 py-3 space-y-2" data-testid="work-order-footer">
            <Button
              className="w-full h-12 text-base font-semibold bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl"
              onClick={handleOnSite}
              disabled={onSiting}
              data-testid="on-site-btn"
            >
              {onSiting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Updating...</>
              ) : (
                <><MapPin className="w-5 h-5 mr-2" />Arrived — On Site</>
              )}
            </Button>
            {!isTechnician && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-blue-600 border-blue-200 hover:bg-blue-50 rounded-xl"
                  onClick={() => setRescheduleOpen(true)}
                  data-testid="reschedule-job-btn"
                >
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                  Reschedule
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                  onClick={handleCancelJob}
                  disabled={cancelling}
                  data-testid="cancel-job-btn"
                >
                  {cancelling ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Cancelling...</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-1.5" />Cancel</>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* on_site / in_progress → Mark Complete */}
        {(task.status === 'on_site' || task.status === 'in_progress') && (
          <div className="shrink-0 border-t bg-white px-4 py-3 space-y-2" data-testid="work-order-footer">
            <PerVisitBillingOverride
              taskId={task.id}
              currentValue={task.chargeOverride}
            />
            <Button
              className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:cursor-not-allowed"
              onClick={() => setConfirmCompleteOpen(true)}
              disabled={completing || failedPhotos.length > 0}
              title={failedPhotos.length > 0 ? 'Retry or remove failed photos before completing' : undefined}
              data-testid="mark-complete-btn-work-order"
            >
              {completing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Completing...</>
              ) : (
                <><CheckCircle2 className="w-5 h-5 mr-2" />Mark Job Complete</>
              )}
            </Button>
            {failedPhotos.length > 0 && (
              <p
                className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center"
                data-testid="failed-photos-block-msg"
              >
                {failedPhotos.length} photo{failedPhotos.length !== 1 ? 's' : ''} failed to upload — retry or remove before completing.
              </p>
            )}
            {/* Undo — revert accidental On Site back to En Route.
                Only rendered from the on_site state; hidden once work has
                actually started (in_progress) and unreachable from
                completed jobs. Never blocks the primary Mark Complete
                path — sits beneath as a low-emphasis secondary action. */}
            {task.status === 'on_site' && (
              <Button
                variant="ghost"
                className="w-full h-9 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded-xl"
                onClick={handleUndoOnSite}
                data-testid="undo-on-site-btn"
                title="Revert to En Route — use this if arrival was logged by mistake or work cannot begin"
              >
                <Truck className="w-3.5 h-3.5 mr-1.5" />
                Undo — back to En Route
              </Button>
            )}
            {/* Reschedule + Cancel — admin / dispatcher only */}
            {!isTechnician && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-blue-600 border-blue-200 hover:bg-blue-50 rounded-xl"
                  onClick={() => setRescheduleOpen(true)}
                  data-testid="reschedule-job-btn"
                >
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                  Reschedule
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-10 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                  onClick={handleCancelJob}
                  disabled={cancelling}
                  data-testid="cancel-job-btn"
                >
                  {cancelling ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Cancelling...</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-1.5" />Cancel</>
                  )}
                </Button>
              </div>
            )}
            {/* Technician sees only cancel, no reschedule */}
            {isTechnician && (
              <Button
                variant="outline"
                className="w-full h-10 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                onClick={handleCancelJob}
                disabled={cancelling}
                data-testid="cancel-job-btn"
              >
                {cancelling ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancelling...</>
                ) : (
                  <><XCircle className="w-4 h-4 mr-2" />Cancel Job</>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Already complete banner */}
        {isCompleted && (
          <div className="shrink-0 border-t bg-green-50 px-4 py-3 flex items-center justify-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              Job Completed{task.completedAt ? ` · ${formatDateTime(task.completedAt)}` : ''}
            </span>
          </div>
        )}

        {/* Cancelled banner */}
        {task.status === 'cancelled' && (
          <div className="shrink-0 border-t bg-red-50 px-4 py-3 flex items-center justify-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-semibold text-red-700">Job Cancelled</span>
          </div>
        )}
      {/*
        Sprint 290 — Technician assignment modal MUST render as a React
        descendant of <SheetContent> so its child Radix Dialog participates
        correctly in Radix's DismissableLayer stack. When it was a sibling
        of <Sheet>, tapping the inner Dialog's X caused Sheet's document-level
        pointerDownOutside handler to fire (because onPointerDownCapture,
        which flips the "inside my React tree" flag, follows the React tree
        via synthetic events — not the DOM tree). The child Dialog is still
        DOM-portalled to document.body, so visual stacking is unaffected.
        See bug_verification_290.json for the failing evidence.
      */}
      <JobAssignModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        task={task}
      />

      {/* Reschedule modal — same rationale: kept inside SheetContent to
          preserve DismissableLayer stack semantics. */}
      <RescheduleJobModal
        task={task}
        isOpen={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
      />
      </SheetContent>
    </Sheet>

    {/* Completion safeguard */}
    <ConfirmCompleteDialog
      open={confirmCompleteOpen}
      onOpenChange={setConfirmCompleteOpen}
      clientName={getDisplayName(client, '')}
      taskId={task?.id}
      onConfirm={() => { setConfirmCompleteOpen(false); handleMarkComplete(); }}
    />

    {/* Customize This Visit — items-only override for the current work order */}
    <Dialog open={itemsCustomizeOpen} onOpenChange={(open) => !open && setItemsCustomizeOpen(false)}>
      <DialogContent className="max-w-lg" data-testid="customize-visit-modal">
        <DialogHeader>
          <DialogTitle>Customize This Visit</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-gray-500">
            Changes apply only to this specific visit. The customer&apos;s Service Program and future visits are unaffected. Undo restores the items this work order was originally generated with.
          </p>
          <DefaultItemsPicker
            value={customizeDraftItems}
            onChange={setCustomizeDraftItems}
            testIdPrefix="customize-visit-items"
          />
          <div>
            <label className="text-xs text-gray-500 block mb-1">Visit-Specific Instructions (optional)</label>
            <Textarea
              value={customizeVisitInstructions}
              onChange={(e) => setCustomizeVisitInstructions(e.target.value)}
              placeholder="e.g., Skip the rear vegetable garden today. Call before entering the side gate."
              rows={3}
              maxLength={4000}
              data-testid="customize-visit-instructions"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Add instructions or exceptions that apply only to this visit. The original Service Program and future visits are unchanged.
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Reason (optional)</label>
            <input
              type="text"
              value={customizeReason}
              onChange={(e) => setCustomizeReason(e.target.value)}
              placeholder="e.g., substituted per site conditions"
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:border-indigo-400"
              maxLength={280}
              data-testid="customize-visit-reason"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => setItemsCustomizeOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveItemOverride}
            disabled={savingOverride}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="customize-visit-save-btn"
          >
            {savingOverride ? 'Saving…' : 'Save Customization'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Photo lightbox — tap thumbnail to open, tap anywhere / × to close. No gallery. */}
    {lightboxPhoto && (
      <div
        className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
        onClick={() => setLightboxPhoto(null)}
        data-testid="photo-lightbox"
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center active:bg-white/20"
          aria-label="Close photo"
          data-testid="photo-lightbox-close"
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={lightboxPhoto.url}
          alt="Site photo"
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

export default WorkOrderSheet;
